<template>
  <div class="floor-param-types">
    <CustomTable
      title="Параметры этажа"
      :rowsData="data"
      :columns="floorPlanTypesColumns"
      :loading="loading"
      :show-search="true"
      :custom-body="true"
      :sticky-header="true"
      :showHeaderAdd="true"
      :on-add-click="addNewItem"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.param_id }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            <input
              v-if="editingId === props.row.param_id"
              v-model="editForm.param_name"
              class="form-control"
              type="text"
              required
            />
            <span v-else>{{ props.row.param_name }}</span>
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            <input
              v-if="editingId === props.row.param_id"
              v-model="editForm.param_code"
              class="form-control"
              type="text"
              required
            />
            <span v-else>{{ props.row.param_code }}</span>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <CustomSelect
              v-if="editingId === props.row.param_id"
              :key="`unit-${props.row.param_id}`"
              :options="unitsOptions"
              :value="editForm.unit_id"
              @change="(value) => editForm.unit_id = value"
              label="Выберите единицу измерения"
              optionValueKey="unit_id"
              optionLabelKey="unit_name"
              :clearable="false"
              :required="true"
            />
            <span v-else>{{ props.row.unit_name }}</span>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <div class="action-buttons">
              <button
                v-if="editingId === props.row.param_id"
                @click="saveItem"
                class="btn btn-success btn-sm"
                :disabled="saving"
              >
                Сохранить
              </button>
              <button
                v-if="editingId === props.row.param_id"
                @click="cancelEdit"
                class="btn btn-secondary btn-sm"
              >
                Отмена
              </button>
              <button
                v-else
                @click="editItem(props.row)"
                class="btn btn-primary btn-sm"
              >
                Редактировать
              </button>
            </div>
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <CustomIcon
              icon="trash"
              :approve-mode="true"
              :loading="!!deleteLoading[props.row.param_id]"
              :approve="() => onDeleteSubmit(props.row.param_id)"
            />
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script>
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomSelect from "@/components/form/CustomSelect";
import CustomIcon from "@/components/common/CustomIcon";
import { getFloorPlanTypes, updateFloorPlanType, deleteFloorPlanType, getParams, getUnits, floorPlanTypesColumns } from "./services";

export default {
  name: "FloorParamTypes",
  components: { CustomTable, CustomTableCell, CustomSelect, CustomIcon },
  data() {
    return {
      floorPlanTypesColumns,
      data: [],
      loading: false,
      saving: false,
      editingId: null,
      editForm: {
        param_id: null,
        param_name: "",
        param_code: "",
        unit_id: null,
        unit_name: "",
      },
      paramsOptions: [],
      unitsOptions: [],
      deleteLoading: {},
      deleting: false,
    };
  },
  async mounted() {
    await this.loadData();
    await this.loadOptions();
  },
  methods: {
    async loadUnits() {
      const result = await getUnits();
      if (!result) return;
      this.unitsOptions = result || [];
    },
    async loadData() {
      this.loading = true;
      const result = await getFloorPlanTypes();
      this.loading = false;
      if (!result) return;
      this.data = result || [];
    },

    async loadOptions() {
      const [paramsResult, unitsResult] = await Promise.all([
        getParams(),
        getUnits(),
      ]);
      if (!paramsResult || !unitsResult) return;
      this.paramsOptions = paramsResult || [];
      this.unitsOptions = unitsResult || [];
    },

    editItem(item) {
      this.editingId = item.param_id;
      this.editForm = { ...item };
    },

    addNewItem() {
      if (this.editingId !== null) {
        return;
      }

      const newItem = {
        param_id: null,
        param_name: "",
        param_code: "",
        unit_id: null,
        unit_name: "",
      };
      this.data.unshift(newItem);
      this.editItem(newItem);
    },

    async saveItem() {
      if (!this.editForm.param_name?.trim()) {
        this.$q.notify({
          type: 'negative',
          message: 'Название параметра этажа обязательно для заполнения'
        });
        return;
      }
      if (!this.editForm.param_code?.trim()) {
        this.$q.notify({
          type: 'negative',
          message: 'Код параметра этажа обязателен для заполнения'
        });
        return;
      }
      if (!this.editForm.unit_id) {
        this.$q.notify({
          type: 'negative',
          message: 'Единица измерения обязательна для выбора'
        });
        return;
      }

      this.saving = true;
      const result = await updateFloorPlanType(this.editForm);
      this.saving = false;
      if (!result) return;
      await this.loadData();
      this.editingId = null;
      this.resetEditForm();
    },

    cancelEdit() {
      if (this.editForm.param_id === 0) {
        this.data = this.data.filter(item => item.param_id !== 0);
      }
      this.editingId = null;
      this.resetEditForm();
    },

    resetEditForm() {
      this.editForm = {
        param_id: null,
        param_name: "",
        param_code: "",
        unit_id: null,
        unit_name: "",
      };
    },
    async onDeleteSubmit(param_id) {
      if (this.deleteLoading[param_id]) return;
      this.deleting = true;
      this.deleteLoading = { ...this.deleteLoading, [param_id]: true };
      const res = await deleteFloorPlanType(param_id);
      this.deleteLoading = { ...this.deleteLoading, [param_id]: false };
      this.deleting = false;
      if (!res) return;
      this.data = this.data.filter(row => row.param_id !== param_id);
    },
  },
};
</script>

<style scoped lang="scss">
.floor-param-types {
  .form-control {
    width: 100%;
    padding: 4px 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  .color-display {
    width: 30px;
    height: 20px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  .action-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;

    .btn {
      padding: 4px 8px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;

      &.btn-primary {
        background-color: #007bff;
        color: white;
      }

      &.btn-success {
        background-color: #28a745;
        color: white;
      }

      &.btn-secondary {
        background-color: #6c757d;
        color: white;
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
}
</style>
